import React from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Send, Copy, FileCheck, Link2, Check, Download, PenLine } from "lucide-react";

const GHOST = "rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800";
const FIELD = "mt-1 border-zinc-800 bg-zinc-950";

export function QuoteToolbar({ id, quote, onSave, onSend, onCopyLink, onPdf, onSign, onDuplicate, onInvoice, onDelete }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">{id ? (quote?.is_invoice ? quote.invoice_number : quote?.number) : "Nouveau devis"}</h1>
        {quote && <div className="mt-2"><StatusBadge status={quote.status} type="quote" /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="quote-save" onClick={onSave} variant="outline" className={GHOST}><Check className="mr-1 h-4 w-4" />Enregistrer</Button>
        <Button data-testid="quote-send" onClick={onSend} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Send className="mr-1 h-4 w-4" />Envoyer</Button>
        {id && quote && (
          <>
            <Button onClick={onCopyLink} variant="outline" className={GHOST}><Link2 className="mr-1 h-4 w-4" />Lien portail</Button>
            <Button data-testid="quote-pdf" onClick={onPdf} variant="outline" className={GHOST}><Download className="mr-1 h-4 w-4" />PDF</Button>
            {quote.signature?.status === "done"
              ? <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-400"><Check className="mr-1 h-4 w-4" />Signé</span>
              : <Button data-testid="quote-sign" onClick={onSign} variant="outline" className={GHOST}><PenLine className="mr-1 h-4 w-4" />Faire signer</Button>}
            <Button onClick={onDuplicate} variant="outline" className={GHOST}><Copy className="mr-1 h-4 w-4" />Dupliquer</Button>
            {quote.status === "accepte" && !quote.is_invoice && <Button data-testid="quote-invoice" onClick={onInvoice} className="rounded-full bg-emerald-600 font-semibold text-white hover:bg-emerald-500"><FileCheck className="mr-1 h-4 w-4" />Facturer</Button>}
            <Button data-testid="quote-delete" onClick={onDelete} variant="outline" className="rounded-full border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300"><Trash2 className="mr-1 h-4 w-4" />Supprimer</Button>
          </>
        )}
      </div>
    </div>
  );
}

function PagePicker({ kind, label, value, pages, onSelect }) {
  return (
    <div>
      <Label className="text-zinc-400">{label}</Label>
      <Select value={value || "default"} onValueChange={(v) => onSelect(kind, v === "default" ? null : v)}>
        <SelectTrigger data-testid={`quote-${kind}-page`} className={FIELD}><SelectValue /></SelectTrigger>
        <SelectContent className="border-zinc-800 bg-zinc-900">
          <SelectItem value="default">Défaut (paramètres)</SelectItem>
          {pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.category ? ` · ${p.category}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function QuoteInfoCard({ form, setForm, contacts, linkedEvent, pagesOf, onSelectPage }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      {linkedEvent && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#FF6A00]/25 bg-[#FF6A00]/5 px-3 py-2 text-sm" data-testid="quote-linked-event">
          <span className="text-zinc-500">Événement :</span><span className="font-medium text-white">{linkedEvent.title}</span>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-zinc-400">Client</Label>
          <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
            <SelectTrigger data-testid="quote-contact" className={FIELD}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900">{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-zinc-400">Intitulé</Label><Input className={FIELD} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <PagePicker kind="opening" label="Page d'ouverture" value={form.opening_page_id} pages={pagesOf("opening")} onSelect={onSelectPage} />
          <PagePicker kind="closing" label="Page de clôture" value={form.closing_page_id} pages={pagesOf("closing")} onSelect={onSelectPage} />
        </div>
      </div>
    </div>
  );
}
